import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

interface ScheduleDay { dayOff: boolean; start: string; end: string; breakMins: number; }

export interface WorkPattern {
  hoursPerWeek?: number;
  monday?: number; tuesday?: number; wednesday?: number; thursday?: number;
  friday?: number; saturday?: number; sunday?: number;
  days?: ScheduleDay[] | null;
}

interface Report { id: number; date: string; startTime: string; endTime: string; status: string; }
interface ToilEntry { total_sum: number; }

interface Props { auth: boolean; userId: number | null; pattern?: WorkPattern | null; }

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const minutesBetween = (start?: string, end?: string) => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  return Math.max(0, (eh * 60 + em) - (sh * 60 + sm));
};

// Target hours per weekday (Mon=0), same rules as backend hr_absence.service dayHours()
function patternHours(p?: WorkPattern | null): number[] {
  if (!p) return [0, 1, 2, 3, 4].map(() => 7.4).concat([0, 0]);
  if (Array.isArray(p.days) && p.days.length === 7) {
    return p.days.map(d => (d.dayOff ? 0 : Math.max(0, minutesBetween(d.start, d.end) - (d.breakMins || 0)) / 60));
  }
  const arr = [p.monday, p.tuesday, p.wednesday, p.thursday, p.friday, p.saturday, p.sunday].map(v => v ?? 0);
  if (arr.some(v => v > 0)) return arr;
  const perDay = (p.hoursPerWeek ?? 0) / 5;
  return [perDay, perDay, perDay, perDay, perDay, 0, 0];
}

function weekStart(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
}

const hoursOf = (r: Report) => Math.max(0, (new Date(r.endTime).getTime() - new Date(r.startTime).getTime()) / 3600000);
const fmt = (n: number) => String(+n.toFixed(1));

const MyWeekCard: React.FC<Props> = ({ auth, userId, pattern }) => {
  const [reports, setReports] = useState<Report[]>([]);
  const [toil, setToil] = useState<number | null>(null);

  const mon = useMemo(() => weekStart(), []);
  const todayIdx = (new Date().getDay() + 6) % 7;

  const load = useCallback(async () => {
    const sun = new Date(mon); sun.setDate(mon.getDate() + 7);
    const [repRes, toilRes] = await Promise.allSettled([
      axios.get('time-reports'),
      userId ? axios.get(`toils/user/${userId}`) : Promise.reject(),
    ]);
    if (repRes.status === 'fulfilled' && Array.isArray(repRes.value.data)) {
      setReports((repRes.value.data as Report[]).filter(r => { const d = new Date(r.date); return d >= mon && d < sun; }));
    }
    if (toilRes.status === 'fulfilled' && Array.isArray(toilRes.value.data)) {
      setToil((toilRes.value.data as ToilEntry[]).reduce((s, t) => s + (t.total_sum || 0), 0));
    }
  }, [mon, userId]);

  useEffect(() => { if (auth) load(); }, [auth, load]);

  const target = useMemo(() => patternHours(pattern), [pattern]);
  const logged = useMemo(() => {
    const arr = [0, 0, 0, 0, 0, 0, 0];
    reports.forEach(r => { arr[(new Date(r.date).getDay() + 6) % 7] += hoursOf(r); });
    return arr;
  }, [reports]);

  // Pattern days always; any other day only when it has logged hours (max 7)
  const shown = DAYS.map((_, i) => i).filter(i => target[i] > 0 || logged[i] > 0);
  const weekTarget = target.reduce((a, b) => a + b, 0);
  const total = logged.reduce((a, b) => a + b, 0);
  const scale = Math.max(9, ...shown.map(i => Math.max(target[i], logged[i])));

  const statuses = reports.map(r => r.status);
  const status = statuses.includes('REJECTED') ? 'Returned'
    : statuses.includes('PENDING') ? 'Submitted'
    : statuses.length > 0 && statuses.every(s => s === 'FULFILLED') ? 'Approved'
    : 'Draft';
  const footText = status;

  return (
    <div className="dashboard-card mw-card">
      <div className="mw-head">
        <div className="mw-ic"><AccessTimeIcon /></div>
        <span className="mw-title">My week</span>
        <div style={{ flex: 1 }} />
        {toil !== null && (
          <span className={`mw-toil${toil < 0 ? ' neg' : ''}`}>
            <small>TOIL</small>{toil > 0 ? '+' : ''}{fmt(toil)}h
          </span>
        )}
      </div>

      <div className="mw-hero">
        <span className="mw-num">{fmt(total)}</span>
        <span className="mw-of">of {fmt(weekTarget)}h</span>
      </div>
      <div className="mw-bar"><i style={{ width: `${weekTarget > 0 ? Math.min(100, (total / weekTarget) * 100) : 0}%` }} /></div>

      <div className="mw-cols" style={{ gridTemplateColumns: `repeat(${shown.length}, 1fr)` }}>
        {shown.map(i => {
          const extra = target[i] === 0;
          const st = extra ? 'extra' : i === todayIdx ? 'today' : i > todayIdx ? 'fut' : logged[i] === 0 ? 'miss' : 'done';
          const showBar = st !== 'fut' && st !== 'miss';
          return (
            <div key={i} className={`mw-col ${st}`}>
              <div className="mw-stk">
                {showBar && <i style={{ height: `${(logged[i] / scale) * 100}%` }} />}
                {!extra && <span className="mw-tl" style={{ bottom: `${(target[i] / scale) * 100}%` }} />}
              </div>
              <b>{st === 'fut' ? '–' : fmt(logged[i])}</b>
              <small>{DAYS[i]}</small>
            </div>
          );
        })}
      </div>

      <div className="mw-foot">
        <span className="mw-status">{footText}</span>
        <Link to="/timesheets" className="mw-link">Timesheets <ArrowForwardIcon /></Link>
      </div>
    </div>
  );
};

export default MyWeekCard;
